import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import PaymentService, { PaymentFormPayload } from '../../../services/payment.service';
import SubscriptionService from '../../../services/subscription.service';
import { Modules, AuthRoutes } from '../../../enums';

interface SubscriptionOption {
  id: number;
  subscription_no: string;
  tenant_name: string | null;
  amount: string;
  currency_id: number;
  currency_code: string | null;
}

interface PaymentFormState {
  subscription_id: number | '';
  amount: number | string;
  currency_id: number | '';
  payment_date: string;
  payment_mode: PaymentFormPayload['payment_mode'] | '';
  reference_no: string;
  notes: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const initialForm: PaymentFormState = {
  subscription_id: '',
  amount: '',
  currency_id: '',
  payment_date: today(),
  payment_mode: '',
  reference_no: '',
  notes: '',
};

const PAYMENT_MODES: PaymentFormPayload['payment_mode'][] = ['cash', 'bank_transfer', 'cheque', 'other'];

const PaymentForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState<PaymentFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<SubscriptionOption[]>([]);
  const [tenantName, setTenantName] = useState('');

  useEffect(() => {
    SubscriptionService.list({ limit: 100 }).then((response: any) => {
      setSubscriptions(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    const subscriptionIdParam = searchParams.get('subscriptionId');
    if (subscriptionIdParam) {
      setFormData((prev) => ({ ...prev, subscription_id: Number(subscriptionIdParam) }));
    }
  }, [searchParams]);

  const selectedSubscription = useMemo(
    () => subscriptions.find((s) => s.id === formData.subscription_id),
    [subscriptions, formData.subscription_id]
  );

  useEffect(() => {
    if (!selectedSubscription) return;
    setFormData((prev) => ({
      ...prev,
      amount: prev.amount || selectedSubscription.amount,
      currency_id: prev.currency_id || selectedSubscription.currency_id,
    }));
    setTenantName(selectedSubscription.tenant_name ?? '');
  }, [selectedSubscription]);

  const handleSubscriptionChange = useCallback(
    (e: any) => {
      const subscriptionId = e.target.value === '' ? '' : Number(e.target.value);
      const subscription = subscriptions.find((s) => s.id === subscriptionId);
      setFormData((prev) => ({
        ...prev,
        subscription_id: subscriptionId,
        amount: subscription ? subscription.amount : '',
        currency_id: subscription ? subscription.currency_id : '',
      }));
      setTenantName(subscription?.tenant_name ?? '');
    },
    [subscriptions]
  );

  const handleChange = useCallback((e: any, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  const validate = useCallback(
    (data: PaymentFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.subscription_id) nextErrors.subscription_id = t('payments.form.pageTexts.fields.subscription.errors.required');
      const amount = Number(data.amount);
      if (!data.amount || !(amount > 0)) nextErrors.amount = t('payments.form.pageTexts.fields.amount.errors.invalid');
      if (!data.payment_date) nextErrors.payment_date = t('payments.form.pageTexts.fields.paymentDate.errors.required');
      if (!data.payment_mode) nextErrors.payment_mode = t('payments.form.pageTexts.fields.paymentMode.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: PaymentFormPayload = {
          subscription_id: Number(formData.subscription_id),
          amount: Number(formData.amount),
          currency_id: Number(formData.currency_id),
          payment_date: formData.payment_date,
          payment_mode: formData.payment_mode as PaymentFormPayload['payment_mode'],
          reference_no: formData.reference_no || undefined,
          notes: formData.notes || undefined,
        };
        await PaymentService.create(payload);
        toast.success(<Toast message={t('payments.form.pageTexts.addSuccess')} />, { icon: false });
        navigate(`${Modules.home}${AuthRoutes.payments}`);
      } finally {
        setLoading(false);
      }
    },
    [formData, navigate, validate, t]
  );

  return (
    <div className="card mb-6">
      <h5 className="card-header">{t('payments.form.pageTexts.addTitle')}</h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-5">
          <label className="form-label d-block" htmlFor="subscription_id">
            {t('payments.form.pageTexts.fields.subscription.label')}
          </label>
          <select
            id="subscription_id"
            name="subscription_id"
            className={`form-select ${errors.subscription_id ? 'is-invalid' : ''}`}
            value={formData.subscription_id}
            onChange={handleSubscriptionChange}
          >
            <option value="">{t('payments.form.pageTexts.fields.subscription.placeholder')}</option>
            {subscriptions.map((subscription) => (
              <option key={subscription.id} value={subscription.id}>
                {subscription.subscription_no} — {subscription.tenant_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <Input id="tenant_name" name="tenant_name" label={t('payments.form.pageTexts.fields.tenant.label')} value={tenantName} disabled />
        </div>

        <div className="col-md-3">
          <Input
            id="payment_date"
            name="payment_date"
            type="date"
            label={t('payments.form.pageTexts.fields.paymentDate.label')}
            required
            value={formData.payment_date}
            error={!!errors.payment_date}
            errorMessage={errors.payment_date}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3">
          <Input
            id="amount"
            name="amount"
            type="number"
            min="0.01"
            label={t('payments.form.pageTexts.fields.amount.label')}
            required
            value={formData.amount}
            error={!!errors.amount}
            errorMessage={errors.amount}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3">
          <Input id="currency_id" name="currency_id" label={t('payments.form.pageTexts.fields.currency.label')} value={selectedSubscription?.currency_code ?? ''} disabled />
        </div>

        <div className="col-md-3">
          <label className="form-label d-block" htmlFor="payment_mode">
            {t('payments.form.pageTexts.fields.paymentMode.label')}
          </label>
          <select
            id="payment_mode"
            name="payment_mode"
            className={`form-select ${errors.payment_mode ? 'is-invalid' : ''}`}
            value={formData.payment_mode}
            onChange={(e) => handleChange(e, 'payment_mode')}
          >
            <option value="">{t('payments.form.pageTexts.fields.paymentMode.placeholder')}</option>
            {PAYMENT_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(`payments.form.pageTexts.fields.paymentMode.options.${mode}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <Input
            id="reference_no"
            name="reference_no"
            label={t('payments.form.pageTexts.fields.referenceNo.label')}
            placeholder={t('payments.form.pageTexts.fields.referenceNo.placeholder')}
            value={formData.reference_no}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('payments.form.pageTexts.fields.notes.label')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('payments.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.payments}`)}
          />
          <Button type="submit" className="btn btn-primary" text={t('payments.form.pageTexts.buttons.save')} loading={loading} />
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
